import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);

  const productCategories = [
    {
      title: 'Water Treatment',
      viewAll: '/solutions/water-treatment',
      items: [
        { name: 'RO Plants (2000–10000 LPH)', path: '/products/2000-lph-ro-plant' },
        { name: 'Water Treatment Plant (WTP)', path: '/products/water-treatment-plant' },
        { name: 'Sewage Treatment Plant (STP)', path: '/products/sewage-treatment-plant' },
        { name: 'Effluent Treatment Plant (ETP)', path: '/products/effluent-treatment-plant' },
        { name: 'MBR Plant', path: '/products/mbr-plant' },
        { name: 'ZLD / MEE / ATFD', path: '/products/zld-plant' }
      ]
    },
    {
      title: 'Bottling Machinery',
      viewAll: '/solutions/bottling-machinery',
      items: [
        { name: 'Water Filling / RFC Machines', path: '/products/60-bpm-water-filling-machine' },
        { name: '30–200 BPM Range', path: '/solutions/bottling-machinery' },
        { name: 'Bottle Unscrambler', path: '/products/bottle-unscrambler' },
        { name: 'Conveyor Systems', path: '/products/conveyor-systems' }
      ]
    },
    {
      title: 'Packaging Machinery',
      viewAll: '/solutions/packaging-machinery',
      items: [
        { name: 'Shrink Packaging Machine', path: '/products/shrink-packaging-machine' },
        { name: 'Sticker Labelling Machine', path: '/products/sticker-labelling-machine' },
        { name: 'Batch Coding Machine', path: '/products/batch-coding-machine' },
        { name: 'Cartoning / Secondary Packaging', path: '/products/cartoning-secondary-packaging' }
      ]
    },
    {
      title: 'Beverage Plants',
      viewAll: '/solutions/beverage-plants',
      items: [
        { name: 'Mineral Water Plants (20–200 BPM)', path: '/solutions/beverage-plants' },
        { name: 'Juice Plant', path: '/products/juice-plant' },
        { name: 'CSD / Soft Drink Plant', path: '/products/csd-soft-drink-plant' }
      ]
    },
    {
      title: 'Bottle Manufacturing',
      viewAll: '/solutions/bottle-manufacturing',
      items: [
        { name: 'PET Bottle Blowing Machine', path: '/products/pet-bottle-blowing-machine' },
        { name: 'Air Compressors', path: '/products/air-compressors' },
        { name: 'Air Dryers & Chillers', path: '/products/air-dryers-chillers' }
      ]
    }
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-alden-green text-white py-2 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-6">
              <a href="tel:+919351292512" className="flex items-center gap-2 hover:text-yellow-300 transition-colors">
                <Phone className="w-4 h-4" />
                <span>+91 93512 92512</span>
              </a>
              <a href="tel:+919351002512" className="hidden md:flex items-center gap-2 hover:text-yellow-300 transition-colors">
                <Phone className="w-4 h-4" />
                <span>+91 93510 02512</span>
              </a>
              <a href="mailto:info@aldentechnologies.in" className="flex items-center gap-2 hover:text-yellow-300 transition-colors">
                <Mail className="w-4 h-4" />
                <span>info@aldentechnologies.in</span>
              </a>
            </div>
            <div>
              <span className="text-yellow-300">Turnkey Water, Beverage &amp; Packaging Engineering Solutions</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-3 md:py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_turnkey-plants-1/artifacts/betwu2y9_ALDEN%20LOGO%20PNG.png" 
                alt="Alden Technologies" 
                className="h-12 md:h-16"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
              <Link to="/" className="px-3 xl:px-4 py-2 text-gray-700 hover:text-alden-green font-semibold transition-colors">
                Home
              </Link>
              <Link to="/about-us" className="px-3 xl:px-4 py-2 text-gray-700 hover:text-alden-green font-semibold transition-colors">
                About
              </Link>
              
              {/* Products Mega Menu */}
              <div 
                className="relative"
                onMouseEnter={() => setIsProductsOpen(true)}
                onMouseLeave={() => setIsProductsOpen(false)}
              >
                <button className="px-3 xl:px-4 py-2 text-gray-700 hover:text-alden-green font-semibold transition-colors flex items-center gap-1">
                  Products
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {isProductsOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white shadow-2xl rounded-lg border-2 border-alden-green/20 p-6 w-[860px] z-50">
                    <div className="grid grid-cols-5 gap-5">
                      {productCategories.map((category) => (
                        <div key={category.title}>
                          <Link to={category.viewAll} className="font-bold text-alden-green mb-3 text-sm block hover:underline">
                            {category.title}
                          </Link>
                          <ul className="space-y-2">
                            {category.items.map((item) => (
                              <li key={`${category.title}-${item.name}`}>
                                <Link 
                                  to={item.path}
                                  className="text-gray-700 hover:text-alden-green text-xs block py-1 transition-colors"
                                >
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link to="/manufacturing-facility" className="px-3 xl:px-4 py-2 text-gray-700 hover:text-alden-green font-semibold transition-colors">
                Manufacturing
              </Link>
              <Link to="/installation-gallery" className="px-3 xl:px-4 py-2 text-gray-700 hover:text-alden-green font-semibold transition-colors">
                Projects
              </Link>
              <Link to="/blog" className="px-3 xl:px-4 py-2 text-gray-700 hover:text-alden-green font-semibold transition-colors">
                Blog
              </Link>
              <Link to="/after-sales-service" className="px-3 xl:px-4 py-2 text-gray-700 hover:text-alden-green font-semibold transition-colors">
                Service
              </Link>
              <Link to="/contact" className="px-3 xl:px-4 py-2 text-gray-700 hover:text-alden-green font-semibold transition-colors">
                Contact
              </Link>
            </nav>

            {/* CTA Buttons - Desktop */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-3">
              <a href="https://wa.me/919351292512" target="_blank" rel="noopener noreferrer">
                <Button className="bg-green-500 hover:bg-green-600 text-white px-3 xl:px-4 py-2 text-sm">
                  WhatsApp
                </Button>
              </a>
              <Link to="/contact">
                <Button className="bg-alden-green hover:bg-alden-green-light text-white px-3 xl:px-4 py-2 text-sm">
                  Get Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:text-alden-green"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="container mx-auto px-4 py-4">
              <nav className="flex flex-col gap-2">
                <Link to="/" className="px-4 py-3 text-gray-700 hover:bg-alden-green/10 hover:text-alden-green rounded-lg font-semibold transition-colors" onClick={() => setIsMenuOpen(false)}>
                  Home
                </Link>
                <Link to="/about-us" className="px-4 py-3 text-gray-700 hover:bg-alden-green/10 hover:text-alden-green rounded-lg font-semibold transition-colors" onClick={() => setIsMenuOpen(false)}>
                  About
                </Link>
                
                {/* Mobile Products Submenu */}
                <div className="px-4 py-3">
                  <button 
                    onClick={() => setIsProductsOpen(!isProductsOpen)}
                    className="flex items-center justify-between w-full text-gray-700 hover:text-alden-green font-semibold"
                  >
                    <span>Products</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isProductsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isProductsOpen && (
                    <div className="mt-3 ml-4 space-y-3">
                      {productCategories.map((category) => (
                        <div key={category.title}>
                          <h4 className="font-bold text-alden-green text-sm mb-2">{category.title}</h4>
                          <ul className="space-y-1 ml-3">
                            {category.items.map((item) => (
                              <li key={item.path}>
                                <Link 
                                  to={item.path}
                                  className="text-gray-600 hover:text-alden-green text-sm block py-1"
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Link to="/manufacturing-facility" className="px-4 py-3 text-gray-700 hover:bg-alden-green/10 hover:text-alden-green rounded-lg font-semibold transition-colors" onClick={() => setIsMenuOpen(false)}>
                  Manufacturing
                </Link>
                <Link to="/installation-gallery" className="px-4 py-3 text-gray-700 hover:bg-alden-green/10 hover:text-alden-green rounded-lg font-semibold transition-colors" onClick={() => setIsMenuOpen(false)}>
                  Projects
                </Link>
                <Link to="/blog" className="px-4 py-3 text-gray-700 hover:bg-alden-green/10 hover:text-alden-green rounded-lg font-semibold transition-colors" onClick={() => setIsMenuOpen(false)}>
                  Blog
                </Link>
                <Link to="/after-sales-service" className="px-4 py-3 text-gray-700 hover:bg-alden-green/10 hover:text-alden-green rounded-lg font-semibold transition-colors" onClick={() => setIsMenuOpen(false)}>
                  Service
                </Link>
                <Link to="/contact" className="px-4 py-3 text-gray-700 hover:bg-alden-green/10 hover:text-alden-green rounded-lg font-semibold transition-colors" onClick={() => setIsMenuOpen(false)}>
                  Contact
                </Link>

                {/* Mobile CTA Buttons */}
                <div className="flex flex-col gap-2 mt-4">
                  <a href="https://wa.me/919351292512" target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-green-500 hover:bg-green-600 text-white py-3">
                      WhatsApp Now
                    </Button>
                  </a>
                  <Link to="/contact">
                    <Button className="w-full bg-alden-green hover:bg-alden-green-light text-white py-3">
                      Get Free Quotation
                    </Button>
                  </Link>
                </div>
              </nav>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
